import { z } from "zod";
import { assertUniqueSortOrders, nlWkrCategorySchema } from "./nlWkr.js";
/**
 * Schema for POST /api/v1/accounts-payable/orgs/:orgId/:invoiceId/payment.
 *
 * Wraps the `process_ap_payment` SECURITY DEFINER RPC, which moved behind
 * `verify_backend_token()` in Pattern C (see migration
 * 20260519003153_pattern_c_ap_ar_payment_rpcs.sql). The invoice id lives in
 * the URL path, the rest in the body — mirrors the clearing endpoints.
 */
export const processAPPaymentSchema = z
    .object({
    payment_amount: z.number().positive(),
    // Bank statement uses YYYY-MM-DD; pass-through to a Postgres `date` column.
    payment_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
        message: "payment_date must be YYYY-MM-DD",
    }),
    payment_method: z.string().min(1),
    bank_account_id: z.string().uuid(),
    ap_account_id: z.string().uuid(),
    reference: z.string().nullable().optional(),
    project_id: z.string().uuid().nullable().optional(),
    fund_id: z.string().uuid().nullable().optional(),
})
    .strict();
/**
 * Schemas for the AP invoice lifecycle routes (apps/api/src/routes/accounting/ap-invoices.ts).
 *
 * Wrap approve_ap_invoice / reject_ap_invoice / delete_ap_invoice /
 * void_ap_invoice. approve/void/delete write the ledger, so the frontend must
 * route them through the backend before the RESTRICTIVE verify_backend_token()
 * ledger policy lands (SCRUM-184; HIGH-1/HIGH-2). delete_ap_invoice and
 * void_ap_invoice are converted DEFINER→INVOKER in Phase II.
 */
export const approveAPInvoiceSchema = z
    .object({ notes: z.string().nullable().optional() })
    .strict();
export const rejectAPInvoiceSchema = z
    .object({ reason: z.string().min(1) })
    .strict();
export const voidAPInvoiceSchema = z
    .object({
    void_reason: z.string().optional(),
    reset_to_pending: z.boolean().optional(),
})
    .strict();
/** POST .../ap-invoices/:invoiceId/apply-transaction — apply an existing ledger tx as an AP payment. */
export const applyTransactionToAPInvoiceSchema = z
    .object({
    transaction_id: z.string().uuid(),
    ap_account_id: z.string().uuid(),
})
    .strict();
/**
 * POST .../ap-invoices/:invoiceId/undo-payment — reverse a recorded payment.
 *
 * Deletes the payment journal (void_payment_transaction) so the bill's
 * amount_paid recomputes downward. This is the prerequisite for editing a bill
 * that has been paid: void_ap_invoice refuses `reset_to_pending` while
 * amount_paid > 0.
 */
export const undoAPPaymentSchema = z
    .object({
    payment_transaction_id: z.string().uuid(),
})
    .strict();
/**
 * POST .../ap-invoices/:invoiceId/repoint-payments — move a paid bill's
 * payments onto a new AP account / fund.
 *
 * Wraps repoint_ap_bill_payments: reverses each live manual payment and
 * re-posts an identical one on new_ap_account_id / new_fund_id
 * (audit-preserving), so a bill's AP control account can be changed after
 * payment without stranding the money. The edit flow calls this AFTER voiding
 * the old posting and BEFORE re-approving so approve_ap_invoice's
 * PAYMENT_ACCOUNT_MISMATCH backstop passes. Refused (IMPORT_LINKED) if any live
 * payment came from the bank feed.
 */
export const repointAPPaymentsSchema = z
    .object({
    new_ap_account_id: z.string().uuid(),
    new_fund_id: z.string().uuid().nullable().optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Create / update / status — the Pattern B+ write RPC wrappers
// (migration 20260727110000_ap_bill_write_rpcs.sql)
// ---------------------------------------------------------------------------
/** One AP line item as sent to create/update (generated totals never sent). */
export const apBillLineItemSchema = z
    .object({
    expense_account_id: z.string().uuid(),
    description: z.string(),
    quantity: z.number(),
    unit_price: z.number(),
    tax_rate: z.number(),
    tax_rate_id: z.string().uuid().nullable().optional(),
    tax_treatment: z.string().optional(),
    tax_inclusive: z.boolean().optional(),
    sort_order: z.number().int(),
    exemption_reason: z.string().nullable().optional(),
    exemption_reason_other: z.string().nullable().optional(),
    /** NL WKR classification (jurisdiction-gated in the UI; rides through to nl_wkr_sync_from_lines). */
    wkr_category: nlWkrCategorySchema.optional(),
})
    .strict();
/**
 * Exactly one of business_id / person_id — accounts_payable_payee_check's
 * twin. EXPORTED because the recurring-template schema (`recurring.ts`) reuses
 * the AP create fields via `.omit()`, which drops the `.refine()` below, so it
 * has to re-apply this predicate in its own `superRefine`.
 */
export const apBillPayeeXor = (v) => (v.business_id != null) !== (v.person_id != null);
/**
 * The AP create body BEFORE the payee-XOR refine — a plain `ZodObject`, so it
 * can be `.omit()`-ed and can sit inside a `z.discriminatedUnion` (neither
 * accepts the `ZodEffects` that `.refine()` returns — see the note at
 * `clearing.ts` "z.discriminatedUnion cannot accept a ZodEffects-wrapped
 * member"). `createAPBillSchema` below is this object plus the refine; route
 * validation always goes through THAT. The recurring-template payload variant
 * (`recurring.ts`) omits the two date fields from this one.
 */
export const apBillCreateFieldsSchema = z
    .object({
    invoice_number: z.string().min(1),
    /**
     * `accounts_payable.invoice_date` is a plain `date` column (user-editable
     * for backdating). TRANSITION (one release): the datetime-local shape the
     * pre-conversion form sent is still accepted so a stale web tab open
     * across the deploy keeps working — `'2026-03-15T14:30'::date` casts fine
     * in SQL. Tighten to date-only (`/^\d{4}-\d{2}-\d{2}$/`) next release.
     */
    invoice_date: z.string().regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/, {
        message: "invoice_date must be yyyy-MM-dd or yyyy-MM-dd'T'HH:mm",
    }),
    due_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
        message: "due_date must be YYYY-MM-DD",
    }),
    description: z.string().nullable(),
    ap_account_id: z.string().uuid(),
    line_items: z
        .array(apBillLineItemSchema)
        .min(1)
        .superRefine(assertUniqueSortOrders),
    business_id: z.string().uuid().nullable().optional(),
    person_id: z.string().uuid().nullable().optional(),
    project_id: z.string().uuid().nullable().optional(),
    fund_id: z.string().uuid().nullable().optional(),
    purchase_order_number: z.string().nullable().optional(),
})
    .strict();
/**
 * POST .../ap-invoices — create_ap_bill_with_line_items. organization_id
 * comes from the path (strict body prevents smuggling); created_by is
 * stamped server-side from auth.uid().
 */
export const createAPBillSchema = apBillCreateFieldsSchema.refine(apBillPayeeXor, {
    message: "Exactly one of business_id or person_id is required",
});
/**
 * PATCH .../ap-invoices/:invoiceId — update_ap_bill_with_line_items.
 *
 * FULL EXPLICIT REPLACE: every clearable field is a REQUIRED key with a
 * nullable value — the legacy partial-update's "absent means don't touch"
 * is gone, so a naive partial payload fails validation instead of silently
 * clearing columns. ap_account_id is required NON-null (a metadata rebuild
 * without it would brick approval), and the payee XOR is refined here and
 * enforced by the table CHECK.
 */
export const updateAPBillSchema = z
    .object({
    invoice_number: z.string().min(1),
    /** Same transition regex as create — see `createAPBillSchema`. */
    invoice_date: z.string().regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/, {
        message: "invoice_date must be yyyy-MM-dd or yyyy-MM-dd'T'HH:mm",
    }),
    due_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
        message: "due_date must be YYYY-MM-DD",
    }),
    description: z.string().nullable(),
    ap_account_id: z.string().uuid(),
    line_items: z
        .array(apBillLineItemSchema)
        .min(1)
        .superRefine(assertUniqueSortOrders),
    business_id: z.string().uuid().nullable(),
    person_id: z.string().uuid().nullable(),
    project_id: z.string().uuid().nullable(),
    fund_id: z.string().uuid().nullable(),
    purchase_order_number: z.string().nullable(),
})
    .strict()
    .refine(apBillPayeeXor, {
    message: "Exactly one of business_id or person_id is required",
});
/**
 * PATCH .../ap-invoices/:invoiceId/status — update_ap_bill_status.
 * on_hold | pending (resume) | cancelled (+ required reason). Lifecycle
 * statuses with ledger effects (approve/reject) have their own routes.
 */
export const updateAPBillStatusSchema = z
    .object({
    status: z.enum(["on_hold", "pending", "cancelled"]),
    reason: z.string().optional(),
})
    .strict()
    .refine((v) => v.status !== "cancelled" || (v.reason?.trim().length ?? 0) > 0, {
    message: "Cancellation requires a reason",
});
//# sourceMappingURL=accountsPayable.js.map